<script setup>
import TD from "@/Components/Table/Element.vue";
import SettingsLayout from "@/Layouts/Settings.vue";
import Input from "@/Components/Settings/Input.vue";
import Textarea from "@/Components/Textarea.vue";
import Select from "@/Components/Select.vue";
import Label from "@/Components/Settings/Label.vue";
import HeaderLink from "@/Components/Settings/HeaderLink.vue";
import HeaderButton from "@/Components/Settings/HeaderButton.vue";
import Heading from "@/Components/Settings/Heading.vue";
import { Head, useForm } from "@inertiajs/inertia-vue3";
import { Inertia } from "@inertiajs/inertia";
import { onUnmounted } from "vue";

const page_name = "Behandler/innen";

const props = defineProps({
  errors: Object,
  has_to_pay: Boolean,
  colors: Array,
});

function submitForm() {
  saveBtnClicked = true;
  form.post("/practitioners");
}
let saveBtnClicked = false;

//onUnmounted(
//    Inertia.on("before", (e) => {
//        if (form.isDirty && !saveBtnClicked) {
//            return confirm(
//                "Sie haben ungespeicherte Eingaben. Wollen Sie diese Seite wirklich verlassen?"
//            );
//        }
//    })
//);

const form = useForm({
  firstname: "",
  lastname: "",
  username: "",
  email: "",
  salutation: "f",
  title: "",
  job: "",
  tel: "",
  color: props.colors[0],
  widgetable: true,
});
</script>

<template>
  <SettingsLayout>
    <!-- <Head title="Stammdaten - Grundeinstellungen" /> -->
    <Head :title="`${page_name} - Grundeinstellungen`" />

    <template #breadcrumb>{{ page_name }}</template>
    <template #controls>
      <HeaderLink class="mr-3" href="/einstellungen/behandlerinnen" type="close"
        >Zurück</HeaderLink
      >
      <HeaderButton @click="submitForm()">
        <span v-if="has_to_pay"> Kostenpflichtig anlegen </span>
        <span v-if="!has_to_pay"> Behandler/in anlegen </span>
      </HeaderButton>
    </template>

    <div class="flex">
      <div class="basis-9/12 border-r border-gray-200 pr-16">
        <Heading v-if="!has_to_pay" title="Neue/n Behandler/in anlegen" />
        <Heading
          v-if="has_to_pay"
          title="Neue/n Behandler/in kostenpflichtig anlegen"
        />

        <p class="max-w-2xl text-sm text-oteoText"></p>

        <div class="mt-8 mb-6 text-white font-bold flex items-center">
          <Label class="!mb-0" value="Anrede" />
          <input
            v-model="form.salutation"
            class="ml-4 appearance-none"
            id="s_female"
            type="radio"
            value="f"
          />
          <Label
            class="inline ml-2 !mb-0 font-normal"
            for="s_female"
            value="Frau"
          />

          <input
            v-model="form.salutation"
            class="ml-4"
            id="s_male"
            type="radio"
            value="m"
          />
          <Label
            class="inline ml-2 !mb-0 font-normal"
            for="s_male"
            value="Herr"
          />

          <input
            v-model="form.salutation"
            class="ml-4"
            id="s_none"
            type="radio"
            value="n"
          />
          <Label
            class="inline ml-2 !mb-0 font-normal"
            for="s_none"
            value="Keine Angabe"
          />
        </div>

        <div class="grid grid-cols-2 gap-5 max-w-3xl">
          <div class="">
            <Label for="firstname" value="Vorname" />
            <Input
              id="firstname"
              type="text"
              class="mt-1 block w-full"
              v-model="form.firstname"
              required
              autofocus
              placeholder="Bitte eingeben"
              autocomplete="given-name"
            />
            <div class="mt-2 text-red-600 text-sm" v-if="errors.firstname">
              {{ errors.firstname }}
            </div>
          </div>

          <div class="">
            <Label for="lastname" value="Nachname" />
            <Input
              id="lastname"
              type="text"
              class="mt-1 block w-full"
              v-model="form.lastname"
              required
              placeholder="Bitte eingeben"
              autocomplete="family-name"
            />
            <div class="mt-2 text-red-600 text-sm" v-if="errors.lastname">
              {{ errors.lastname }}
            </div>
          </div>

          <div class="">
            <Label for="title" value="Titel" />
            <Input
              id="title"
              type="text"
              class="mt-1 block w-full"
              v-model="form.title"
              required
              placeholder="Bitte eingeben"
            />
            <div class="mt-2 text-red-600 text-sm" v-if="errors.title">
              {{ errors.title }}
            </div>
          </div>

          <div class="">
            <Label for="job" value="Tätigkeit" />
            <Select v-model="form.job" class="mt-1 block w-full">
              <option disabled value="" class="">Bitte wählen</option>
              <option value="Zahnarzt/in">Zahnarzt/in</option>
              <option value="Zahnmedizinische Prophylaxe">
                Zahnmedizinische Prophylaxe
              </option>
              <option value="Zahnmedizinische Fachangestellte/r">
                Zahnmedizinische Fachangestellte/r
              </option>
            </Select>
            <div class="mt-2 text-red-600 text-sm" v-if="errors.job">
              {{ errors.job }}
            </div>
          </div>

          <div class="">
            <Label class="inline-block" for="username" value="Kürzel" />
            <span class="ml-2 inline-block text-sm font-light"
              >(max. 3 Zeichen)</span
            >
            <Input
              id="username"
              type="text"
              class="mt-1 block w-full"
              v-model="form.username"
              required
              maxlength="3"
              placeholder="Bitte eingeben"
            />
            <div class="mt-2 text-red-600 text-sm" v-if="errors.username">
              {{ errors.username }}
            </div>
          </div>

          <div class="">
            <Label class="inline-block" for="tel" value="Telefonnummer" />
            <span class="ml-2 inline-block text-sm font-light"
              >(Notfall-Kontakt)</span
            >

            <Input
              id="tel"
              type="tel"
              class="mt-1 block w-full"
              v-model="form.tel"
              required
              placeholder="Bitte eingeben"
            />
            <div class="mt-2 text-red-600 text-sm" v-if="errors.tel">
              {{ errors.tel }}
            </div>
          </div>

          <div class="">
            <Label class="inline-block" for="color" value="Farbe" />

            <div class="flex flex-wrap">
              <template v-for="color in props.colors">
                <div class="w-1/3 flex items-center">
                  <input
                    v-model="form.color"
                    class=""
                    :id="'color_' + color"
                    type="radio"
                    :value="color"
                  />
                  <label :for="'color_' + color" class="inline ml-2">
                    <div
                      class="inline-block w-4 h-4 rounded-full"
                      :style="'margin-bottom:-2px;background-color: ' + color"
                    ></div>
                  </label>
                </div>
              </template>
            </div>

            <div class="mt-2 text-red-600 text-sm" v-if="errors.color">
              {{ errors.color }}
            </div>
          </div>

          <div v-if="$page.props.auth.office_canusewidget" class="">
            <Label> Online-Terminvergabe </Label>
            <div class="flex max-w-3xl gap-5 mt-4">
              <Label class="mr-4 inline-block"
                >Behandler/in für Online-Terminvergabe freigeben</Label
              >
              <input v-model="form.widgetable" type="checkbox" checked />
            </div>
            <div class="mt-2 text-red-600 text-sm" v-if="errors.widgetable">
              {{ errors.widgetable }}
            </div>
          </div>
        </div>
      </div>
      <div v-if="has_to_pay" class="basis-3/12 pl-16">
        <p class="mt-8 text-lg">
          Jede/r weitere Behandler/in
          <br />
          <span class="text-oteo-300 font-bold text-lg">4,90 €</span>
        </p>
        <p class="text-s">je Monat zzgl. MwSt</p>
      </div>
    </div>
  </SettingsLayout>
</template>
